import EstablishmentOverviewCard from "@/entities/establishment/ui/EstablishmentOverviewCard"
import EstablishmentPreventionOverview from "@/entities/establishment/ui/EstablishmentPreventionOverview"
import PapripactDocument from "@/entities/document/ui/PapripactDocument"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useResolvedPath,
  useSearchParams
} from "react-router-dom"
import { instanceBackend } from "@/App"
import { createDocumentViewStore, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { Provider } from "react-redux"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import mapStore from "@/entities/map/model/map.store"
import { sessionEdit, sessionEditAccountOptions, sessionEditUserOptions } from "@/redux/_session.actions"
import { AccountColors } from "@/redux/account.types"
import ListDropdown from "@/components/list-dropdown"
import PageLoader from "@/components/page-loader"
import { Document } from "@/entities/document/model/document.types"
import { exportDocumentToPdf } from "@/entities/document/utils/exportDocumentToPdf"
import { formatPhysicalRisk } from "@/entities/physical-risk/model/physical-risk.formatter"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import siteStore from "@/entities/site/model/site.store"
import populationStore from "@/entities/population/model/population.store"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { STATUS_LOADING } from "@/redux/_status.types"
import { getImageUrl } from "@/utils/get-image-url"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faDownload, faHome } from "@fortawesome/free-solid-svg-icons"
import NavItem from "@/components/nav-item"
import Button from "@/components/button"
import DocumentExportModal from "@/entities/document/ui/modal/DocumentExportModal"
import { RisksSpaceProvider, useRisksSpace } from "@/core/risks-space.context"
import DocumentIntroCard from "@/entities/document/ui/DocumentIntroCard"
import PageHeader from "@/components/page-header"
import { userGet } from "@/redux/user.actions"

function getContent(document: Document | null) {
  return document?.content ?? {}
}

function formatDate(date: Date | string | null | undefined) {
  if (!date) return "Non défini"
  return new Date(date).toLocaleDateString("fr-FR")
}

function DocumentPublicNavBar({ currentDocument, versions }: { currentDocument: Document; versions: Document[] }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const basePath = useResolvedPath(".").pathname
  const { getPath } = useRisksSpace()
  const _session = useAppSelector((state) => state._session)
  const detailMatch = pathname.slice(basePath.length).match(/^\/(populations|sites|phy|rps)\/[^/]+\/?$/)
  const backPath = detailMatch
    ? getPath(
        `/${detailMatch[1]}${detailMatch[1] === "sites" && searchParams.get("view") === "other" ? "?view=other" : ""}`
      )
    : null

  function getDocumentPath(version: Document) {
    const latestDocument = versions[0]

    if (latestDocument?.id === version.id) {
      return `/document/lastest?establishmentId=${version.EstablishmentId}`
    }

    return `/document/${version.id}`
  }

  const versionItems = versions.map((version) => ({
    ...version,
    name: `Version du ${formatDate(version.date)}`
  }))

  return (
    <div
      className="flex page-navigation-menutop grey-bor"
      style={{
        background: `linear-gradient(90deg, ${_session.accountColors.brandPrimary} 0%, ${_session.accountColors.brandSecondary} 100%)`
      }}>
      <div className="flex _hover" title="Retour à l'accueil du document" onClick={() => navigate(getPath(""))}>
        {_session.accountOptions.image ? (
          <img
            alt="account"
            className="page-navigation-circle-img"
            src={getImageUrl("Accounts", _session.accountId, _session.logoLastUpdate)}
          />
        ) : (
          <div className="page-navigation-circle-img flex">
            <FontAwesomeIcon style={{ fontSize: 18 }} icon={faHome} className="white-t flex-auto" />
          </div>
        )}
      </div>
      {_session.accountOptions.image && <NavItem icon={faHome} pathname={getPath("")} tooltip="Accueil du document" />}
      {backPath && <NavItem icon={faChevronLeft} pathname={backPath} tooltip="Retour à la liste" />}
      <div
        className="document-public-nav-title"
        style={{
          color: _session.accountColors.brandText
        }}>
        <b>Document unique d’évaluation des risques professionnels</b>
      </div>
      <div className="document-public-nav-actions">
        {versions.length > 1 ? (
          <ListDropdown
            isBold
            isCaretAlwaysVisible
            color={_session.accountColors.brandText}
            value={`Version du ${formatDate(currentDocument.date)}`}
            active={currentDocument.id}
            values={versionItems}
            positionRight
            maxHeight={320}
            onSelect={(version: Document) => {
              window.location.href = getDocumentPath(version)
            }}
          />
        ) : (
          <span className="font-bold" style={{ color: _session.accountColors.brandText }}>
            Version du {formatDate(currentDocument.date)}
          </span>
        )}
      </div>
    </div>
  )
}

export default function RisksDocumentDetailRoute() {
  const { documentId } = useParams()
  const [searchParams] = useSearchParams()
  const establishmentId = searchParams.get("establishmentId")
  //Une autre version repart d'un store vierge ; les pages d'un meme document partagent le sien.
  return <DocumentStoreBoundary key={`${documentId}-${establishmentId}`} />
}

function DocumentStoreBoundary() {
  const { documentId } = useParams()
  const [searchParams] = useSearchParams()
  const storageKey = `document:${documentId}:${searchParams.get("establishmentId") ?? ""}:risksShowCalculations`
  const [store] = useState(() => {
    const documentStore = createDocumentViewStore()
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved !== null) documentStore.dispatch(sessionEditUserOptions("risksShowCalculations", saved === "true"))
    } catch {
      //La consultation reste disponible lorsque le stockage local est bloque.
    }
    return documentStore
  })

  useEffect(() => {
    let previousValue = store.getState()._session.userOptions.risksShowCalculations
    return store.subscribe(() => {
      const value = store.getState()._session.userOptions.risksShowCalculations
      if (value === previousValue) return
      previousValue = value
      try {
        localStorage.setItem(storageKey, String(value))
      } catch {
        //Le choix reste applique au document courant si le stockage est indisponible.
      }
    })
  }, [store, storageKey])

  return (
    <Provider store={store}>
      <DocumentDetail />
    </Provider>
  )
}

function DocumentDetail() {
  const dispatch = useAppDispatch()
  const { documentId } = useParams()
  const [searchParams] = useSearchParams()
  const establishmentId = searchParams.get("establishmentId")
  const basePath = useResolvedPath(".").pathname
  const [document, setDocument] = useState<Document | null>(null)
  const [documentVersions, setDocumentVersions] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isSnapshotReady, setIsSnapshotReady] = useState(false)
  //Memoise : ces valeurs alimentent des deps d'effets et de memos. Sans ca, le repli "?? {}" cree
  //une nouvelle reference a chaque rendu, ce qui redeclenche l'effet qui remplit les slices, donc
  //un nouveau rendu, en boucle.
  const content = useMemo(() => getContent(document), [document])
  const accountRisksSettings = useMemo(
    () => content.accountRisksSettings ?? content.organization ?? {},
    [content.accountRisksSettings, content.organization]
  )
  const riskFamilies = useMemo(() => content.riskFamilies ?? [], [content.riskFamilies])

  //Les pages risques de l'app, en lecture seule, sous le chemin du document. La derniere version
  //s'adresse par establishmentId : chaque lien le garde pour rester sur ce document.
  const getPath = useCallback(
    (path: string) => {
      const [pathname, query] = path.split("?")
      const params = new URLSearchParams(query)
      if (establishmentId) params.set("establishmentId", establishmentId)
      const search = params.toString()
      return `${basePath}${pathname}${search ? `?${search}` : ""}`
    },
    [basePath, establishmentId]
  )
  const risksSpace = useMemo(() => ({ isReadonly: true, getPath }), [getPath])

  //Re-compute the risk values (including the IDTree note cValueNormalized) from the document's own
  //accountRisksSettings snapshot : ce rendu precede l'effet qui pousse ce snapshot dans la session,
  //et c'est le snapshot qui fait foi, pas les reglages courants du compte. Fall back to the raw
  //risks when the snapshot has no usable accountRisksSettings (formatPhysicalRisk needs its
  //riskAttributes).
  const risks = useMemo(() => {
    const rawRisks = content.physicalRisks ?? content.risks ?? []
    if (!accountRisksSettings.riskAttributes) return rawRisks
    const context = { accountRisksSettings, riskFamilies, populations: content.populations ?? [] }
    return rawRisks.map((risk) => formatPhysicalRisk(risk, undefined as any, context as any))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.physicalRisks, content.risks, accountRisksSettings, content.populations, riskFamilies])

  useEffect(() => {
    if (!document) return

    //Ces reglages appartiennent au snapshot, y compris en consultation connectee.
    if (accountRisksSettings.riskAttributes) {
      dispatch(sessionEdit("accountRisksSettings", accountRisksSettings))
    }
    dispatch(riskFamilyStore.actions.setList(content.riskFamilies ?? []))
    dispatch(mapStore.actions.setList(content.maps ?? []))
    dispatch(establishmentStore.actions.setList(content.establishment ? [content.establishment] : []))
    if (content.establishment) dispatch(establishmentStore.actions.setActive(content.establishment))

    dispatch(physicalRiskStore.actions.setList(risks))
    dispatch(siteStore.actions.setList(content.sites ?? []))
    dispatch(populationStore.actions.setList(content.populations ?? []))
    dispatch(preventionActionStore.actions.setList(content.preventionActions ?? []))
    dispatch(userGet(content.users ?? []))
    setIsSnapshotReady(true)
  }, [accountRisksSettings, content, dispatch, document, risks])

  useEffect(() => {
    async function fetchDocumentVersions() {
      if (document?.type === "papripact") return
      const documentEstablishmentId = document?.EstablishmentId ?? document?.content?.establishment?.id
      if (!documentEstablishmentId) return

      const response = await instanceBackend.get(`/public/document/versions?establishmentId=${documentEstablishmentId}`)
      if (Array.isArray(response.data)) {
        setDocumentVersions(response.data)
      }
    }

    fetchDocumentVersions()
  }, [document])

  //Le document s'affiche aux couleurs du compte et avec son logo, sans session : on les demande a part
  async function loadAccountBranding(documentEstablishmentId?: string) {
    if (!documentEstablishmentId) return
    const response = await instanceBackend.get(`/public/establishment?establishmentId=${documentEstablishmentId}`)
    const account = response.data?.account
    if (!account) return

    dispatch(sessionEdit("accountId", account.id))
    dispatch(sessionEdit("accountColors", new AccountColors(account.colors)))
    dispatch(sessionEditAccountOptions("image", account.image))
  }

  useEffect(() => {
    async function fetchDocument() {
      try {
        setIsLoading(true)
        const isLatestRoute = documentId === "lastest"
        const response = await instanceBackend.get(
          isLatestRoute
            ? `/public/document/lastest${establishmentId ? `?establishmentId=${establishmentId}` : ""}`
            : `/public/document/${documentId}`
        )
        if (!response.data || response.data.error) {
          setHasError(true)
        } else {
          await loadAccountBranding(response.data.EstablishmentId ?? response.data.content?.establishment?.id)
          setDocument(response.data)
        }
      } catch {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDocument()
  }, [documentId, establishmentId])

  if (isLoading || (document && !isSnapshotReady)) {
    return (
      <div className="flex min-h-screen app-content-width items-center justify-center bg-[#F3F4F6]">
        <PageLoader status={STATUS_LOADING} />
      </div>
    )
  }

  if (hasError || !document) {
    return (
      <div className="flex min-h-screen app-content-width items-center justify-center bg-slate-100">
        <div className="rounded bg-white p-[30px] text-center shadow">
          <h1 className="text-2xl font-semibold">Document introuvable</h1>
          <p className="grey-t mt-10">Le document demandé n’est pas disponible.</p>
        </div>
      </div>
    )
  }

  if (document.type === "papripact") return <PapripactDocument document={document} />

  return (
    <RisksSpaceProvider value={risksSpace}>
      <DocumentPublicNavBar
        currentDocument={document}
        versions={documentVersions.length ? documentVersions : [document]}
      />
      <Outlet context={document} />
    </RisksSpaceProvider>
  )
}

/** Accueil du document : meme gabarit que le home de l'app, les widgets prennent la hauteur restante. */
export function DocumentHome() {
  const dispatch = useAppDispatch()
  const document = useOutletContext<Document>()
  const _session = useAppSelector((state) => state._session)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const content = getContent(document)
  const establishment = content.establishment ?? {}

  async function downloadDocument() {
    if (isDownloading) return

    setIsExportModalOpen(false)
    setIsDownloading(true)
    try {
      await exportDocumentToPdf(dispatch, document, {
        establishment,
        accountRisksSettings: content.accountRisksSettings ?? content.organization ?? {},
        populations: content.populations ?? [],
        sites: content.sites ?? [],
        session: _session
      })
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="fixed left-0 top-[48px] h-[calc(100%-48px)] app-content-width overflow-y-auto bg-[#F3F4F6]">
      {/* Un seul rythme vertical de 30px : le gap porte l'espacement, pas les marges des composants */}
      <div className="mx-auto box-border flex min-h-full max-w-[1600px] flex-col gap-[30px] p-[30px] [&>.page-header]:mb-0 [&>.page-header]:flex-wrap [&>.page-header]:gap-[16px]">
        <PageHeader title={establishment.name ? `DUERP pour l’établissement ${establishment.name}` : "DUERP"}>
          <Button
            isFullWidth
            className="primary"
            icon={faDownload}
            isLoading={isDownloading}
            onClick={() => setIsExportModalOpen(true)}>
            Télécharger le document
          </Button>
        </PageHeader>

        <div className="establishment-dashboard-grid establishment-dashboard-grid--document">
          <DocumentIntroCard />
          <EstablishmentOverviewCard />
          <EstablishmentPreventionOverview />
        </div>
      </div>
      {isExportModalOpen && (
        <DocumentExportModal
          document={document}
          onClose={() => setIsExportModalOpen(false)}
          onExportPdf={downloadDocument}
        />
      )}
    </div>
  )
}
